import { CashLotItem } from '../api/cash-lot';
import moment from 'moment';

export interface AggregatedCashLotItem {
  items: CashLotItem[];
  dateUnit: string;
  timestamp: number;
}

const getDateRange = (date: Date, numberOfWeeks: number): string => {
  const firstDay = moment(date).startOf("week");
  const lastDay = moment(date).add(numberOfWeeks - 1, "weeks").endOf("week");
  return `${firstDay.format("MM/D")} - ${lastDay.format("MM/D")}`;
};

const getFortnightGroupingKey = (date: Date): string => {
  const weekNumber = moment(date).week();
  const fortnightNumber = weekNumber % 2 === 0 ? weekNumber / 2 : Math.floor(weekNumber / 2) + 1;
  return `${fortnightNumber}/${moment(date).endOf("week").year()}`;
};

const getQuarter = (date: Date): string => {
  const month = date.getMonth() + 1;
  if (month <= 3) {
    return "Q1";
  } else if (month <= 6) {
    return "Q2";
  } else if (month <= 9) {
    return "Q3";
  } else {
    return "Q4";
  }
};

const getGroupingKey = (cashLotItem: CashLotItem, frequency: string) => {
  switch (frequency) {
    case "day": return moment(cashLotItem.period).format("MM/D/YYYY");
    case "week": return moment(cashLotItem.period).format("w/YYYY");
    case "fortnight": return getFortnightGroupingKey(cashLotItem.period);
    case "month": return `${cashLotItem.period.getMonth()}/${cashLotItem.period.getFullYear()}`;
    case "quarter": return `${getQuarter(cashLotItem.period)}/${cashLotItem.period.getFullYear()}`;
    case "year": return `${cashLotItem.period.getFullYear()}`;
    default: return '';
  }
};

const getDateUnit = (cashLotItem: CashLotItem, frequency: string) => {
  switch (frequency) {
    case "day": return moment(cashLotItem.period).format("MM/D");
    case "week": return getDateRange(cashLotItem.period, 1);
    case "fortnight": return getDateRange(cashLotItem.period, 2);
    case "month": return `${cashLotItem.period.toLocaleString('default', { month: 'short' })}`;
    case "quarter": return getQuarter(cashLotItem.period);
    case "year": return `${cashLotItem.period.getFullYear()}`;
    default: return '';
  }
};

export const getAggregatedCashLotItems = (cashLotItems: CashLotItem[], frequency: string): AggregatedCashLotItem[] => {
  const items = new Map<string, AggregatedCashLotItem>();

  for (let cashLotItem of cashLotItems) {
    const groupingKey = getGroupingKey(cashLotItem, frequency);
    if (!items.has(groupingKey)) {
      items.set(groupingKey, {
        items: [],
        dateUnit: getDateUnit(cashLotItem, frequency),
        timestamp: cashLotItem.period.getTime()
      });
    }

    const aggregatedItem = items.get(groupingKey);
    aggregatedItem.items.push(cashLotItem);
  }

  return Array.from(items.values()).sort((a, b) => a.timestamp - b.timestamp);
};